import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { Printer } from 'phosphor-react';
import { toast } from 'sonner';
import { IEstatutoCampoMeta, IEstatutoPasso, useEstatuto } from '../../../../hooks/useEstatuto';
import { PASSOS_CONSTITUICAO } from '../../../estatutoPublico/fieldGroups';

// O estilo mora dentro da lista: vai junto na impressão (janela própria, sem o CSS do Chakra).
const CSS = `
.perguntas { font-family: Inter, Arial, sans-serif; color: #1a202c; font-size: 14px; line-height: 1.45; }
.perguntas h1 { font-size: 19px; font-weight: 700; margin: 0 0 4px; }
.perguntas h2 { font-size: 16px; font-weight: 700; margin: 22px 0 2px; color: #553c9a; }
.perguntas h3 { font-size: 14px; font-weight: 700; margin: 16px 0 0; }
.perguntas p { margin: 0 0 4px; color: #4a5568; }
.perguntas ul { list-style: disc; padding-left: 22px; margin: 4px 0 0; }
.perguntas li { margin: 4px 0; }
.perguntas em { color: #718096; font-style: normal; }
.perguntas small { display: block; color: #718096; font-size: 12.5px; }
@media print { .perguntas h3 { break-after: avoid; } .perguntas li { break-inside: avoid; } }
`;

// Campo que só aparece com outra escolha (o mesmo `.when` do formulário do presidente).
const CONDICAO: Record<string, string> = {
  balancete_trimestral_ate: 'só se o balancete for trimestral',
  local: 'só se a assembleia for presencial ou híbrida',
  plataforma: 'só se a assembleia for virtual ou híbrida',
};

const PESSOAS =
  'De cada fundador: nome completo, sexo, nacionalidade, estado civil, profissão, RG (número e ' +
  'órgão expedidor), CPF, endereço residencial com CEP, e-mail, o cargo de quem foi eleito para a ' +
  'diretoria e se esteve na assembleia. O formulário tem uma planilha modelo para colar a lista.';
const PRESENTES =
  'De cada presente: nome completo, CPF, e-mail, sexo e se está adimplente. Com a constituição ' +
  'no sistema, a lista já vem dos fundadores: é só marcar quem veio. Também dá para colar de uma ' +
  'planilha (nome, CPF, e-mail e sexo).';

const TITULOS = [
  'Estatuto da comissão de formatura: o que vai ser pedido',
  'Encerramento (baixa) da comissão: o que vai ser pedido',
];

// Valor que o formulário já traz (o presidente muda se a turma decidiu outro).
const padrao = (v: unknown) => {
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'boolean') return v ? 'sim' : 'não';
  return String(v);
};

const Campo = ({ m }: { m: IEstatutoCampoMeta }) => {
  const temPadrao = m.default !== null && m.default !== undefined && m.default !== '';
  // Sem obrigatório no back = tem padrão ou depende de outra escolha; "opcional" só sem os dois.
  const notas = [
    CONDICAO[m.name],
    !m.required && !CONDICAO[m.name] && !temPadrao && m.type !== 'boolean' && 'opcional',
    m.type === 'boolean' && 'sim ou não',
    temPadrao && `já vem preenchido: ${padrao(m.default)}`,
  ].filter(Boolean);
  return (
    <li>
      {m.label}
      {notas.length > 0 && <em> ({notas.join('; ')})</em>}
      {m.help_text && <small>{m.help_text}</small>}
      {m.type === 'select' && !!m.options?.length && <small>Opções: {m.options.join(', ')}.</small>}
      {m.type === 'pessoas' && (
        <small>
          {PESSOAS}
          {m.options?.length ? ` Cargos: ${m.options.join(', ')}.` : ''}
        </small>
      )}
      {m.type === 'presentes' && <small>{PRESENTES}</small>}
    </li>
  );
};

const Passos = ({ passos, campos }: { passos: IEstatutoPasso[]; campos: IEstatutoCampoMeta[] }) => {
  const porNome = new Map(campos.map(c => [c.name, c]));
  return (
    <>
      {passos.map((p, i) => (
        <div key={p.titulo}>
          <h3>
            {i + 1}. {p.titulo}
          </h3>
          <p>{p.descricao}</p>
          <ul>
            {p.campos.map(nome => {
              const m = porNome.get(nome);
              return m ? <Campo key={nome} m={m} /> : null;
            })}
          </ul>
        </div>
      ))}
    </>
  );
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // Abre na aba do quadro de onde veio o clique (0 = constituição, 1 = baixa).
  abaInicial?: number;
}

/** O que o presidente vai preencher (constituição e as 2 partes da Baixa), para mandar antes do link. */
export const EstatutoPerguntasModal = ({ isOpen, onClose, abaInicial = 0 }: Props) => {
  const { getPerguntas } = useEstatuto();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['estatuto-perguntas'],
    queryFn: getPerguntas,
    enabled: isOpen,
    staleTime: Infinity,
  });
  const perguntas = data?.data;
  const [aba, setAba] = useState(abaInicial);
  useEffect(() => {
    if (isOpen) setAba(abaInicial);
  }, [isOpen, abaInicial]);
  const folhas = useRef<(HTMLDivElement | null)[]>([]);

  const imprimir = () => {
    // outerHTML: a classe `.perguntas` do estilo está na própria div.
    const html = folhas.current[aba]?.outerHTML;
    if (!html) return;
    const janela = window.open('', '_blank');
    if (!janela) {
      toast.error('O navegador bloqueou a janela de impressão. Libere os pop-ups deste site.');
      return;
    }
    janela.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>${TITULOS[aba]}</title></head>` +
        `<body style="margin:32px">${html}</body></html>`,
    );
    janela.document.close();
    janela.focus();
    janela.print();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Perguntas do formulário</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoading && (
            <Flex justify="center" py={10}>
              <Spinner color="purple.500" />
            </Flex>
          )}
          {isError && (
            <Text color="red.500" fontSize="sm">
              Não foi possível carregar as perguntas. Tente novamente em alguns instantes.
            </Text>
          )}
          {perguntas && (
            <Tabs colorScheme="purple" index={aba} onChange={setAba}>
              <TabList>
                <Tab>Constituição</Tab>
                <Tab>Encerramento (baixa)</Tab>
              </TabList>
              <TabPanels>
                <TabPanel px={1}>
                  <div
                    className="perguntas"
                    ref={el => {
                      folhas.current[0] = el;
                    }}
                  >
                    <style>{CSS}</style>
                    <h1>{TITULOS[0]}</h1>
                    <p>
                      O presidente preenche estes dados no formulário do link. Vale juntar tudo
                      antes de começar.
                    </p>
                    <Passos passos={PASSOS_CONSTITUICAO} campos={perguntas.constituicao} />
                  </div>
                </TabPanel>
                <TabPanel px={1}>
                  <div
                    className="perguntas"
                    ref={el => {
                      folhas.current[1] = el;
                    }}
                  >
                    <style>{CSS}</style>
                    <h1>{TITULOS[1]}</h1>
                    <p>
                      O formulário tem duas partes: a 1ª antes da assembleia (sai o edital) e a 2ª
                      depois dela. Com a constituição no sistema, os dados da associação, da
                      diretoria e a cidade já vêm dela: é só conferir.
                    </p>
                    {perguntas.baixa.map(parte => (
                      <section key={parte.etapa}>
                        <h2>
                          {parte.etapa === 'convocacao'
                            ? '1ª parte: associação e convocação da assembleia'
                            : '2ª parte: a assembleia'}
                        </h2>
                        <Passos passos={parte.passos} campos={parte.schema_campos} />
                      </section>
                    ))}
                  </div>
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
          <Button
            colorScheme="purple"
            leftIcon={<Printer size={16} weight="bold" />}
            onClick={imprimir}
            isDisabled={!perguntas}
          >
            Imprimir ou salvar em PDF
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
